import { BrowserRouter, useLocation } from 'react-router-dom';
import { AppRoutes } from './router';
import { lazy, Suspense } from 'react';
import { useUIStore } from './stores/uiStore';
import { useAuthListener } from './hooks/useAuthListener';
import { ToastProvider } from './components/ToastProvider';
import { useTimezoneDetection } from './hooks/useTimezoneDetection';
import { useAuthStore } from './stores/authStore';
import { useEffect } from 'react';
import { useMemoryStore } from './stores/memoryStore';
import { useGoalForecast } from './hooks/useGoalForecast';
import { useLanguageSync } from './hooks/useLanguageSync';

const ChatBot = lazy(() => import('./pages/dashboard/components/chat/ChatBot'));

function LoadingFallback() {
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);

  return null;
}

function App() {
  useAuthListener();
  useTimezoneDetection();
  useLanguageSync();

  const { fetchGoalForecast } = useGoalForecast()

  const { isChatBotOpen, chatBotInitialMessage, closeChatBot } = useUIStore();
  const { user } = useAuthStore();

  useEffect(() => {
    if (!user) {
      return;
    }

    const { loadMemories, subscribeToMemories, unsubscribeFromMemories } = useMemoryStore.getState();

    loadMemories();
    subscribeToMemories(user.id);
    fetchGoalForecast();

    return () => {
      unsubscribeFromMemories();
    };
  }, [user?.id, fetchGoalForecast]);

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <ScrollToTop />
        <AppRoutes />
      </Suspense>
      <Suspense fallback={null}>
        <ChatBot
          onClose={closeChatBot}
          isOpen={isChatBotOpen}
          initialMessage={chatBotInitialMessage}
        />
      </Suspense>
      <ToastProvider />
    </BrowserRouter>
  );
}

export default App;